import {all, call, put, select, takeLatest, fork} from 'redux-saga/effects';
import {leerplatformSettingsStore} from '../stores/leerplatformSettingsStore';

export const FETCH_SETTINGS = 'FETCH_SETTINGS';
export const SAVE_SETTINGS = 'SAVE_SETTINGS';
export const ADD_ADMIN = 'ADD_ADMIN';
export const ADD_REVIEWER = 'ADD_REVIEWER';
export const REMOVE_ADMIN = 'REMOVE_ADMIN';
export const REMOVE_REVIEWER = 'REMOVE_REVIEWER';

const getHeaders = (user) => {
    if (user) {
        return {
            Authorization: `Bearer ${user.access_token}`,
        };
    } else return {};
};

function* fetchSkillsSettingsAndUpdate(environment, portalSettings, headers) {
    if (!portalSettings.skillsEnabled) {
        yield put({
            type: 'MINERVASETTINGS_SKILLS_UPDATED',
            payload: {
                skillsSettings: {},
                skillsApiUrl: null,
            },
        });
        return;
    }

    try {
        const skillsResponse = yield call(
            fetchSkillsSettings,
            environment.skillsApiUrl,
            portalSettings.portalIdentifier,
            headers,
        );

        if (skillsResponse) {
            yield put({
                type: 'MINERVASETTINGS_SKILLS_UPDATED',
                payload: {
                    skillsSettings: skillsResponse,
                    skillsApiUrl: environment.skillsApiUrl,
                },
            });
        }
    } catch (error) {
        console.error('Failed to fetch skills settings:', error);
    }
}

function* doFetchSettings() {
    const state = yield select();
    const oidc = state.oidc;
    const idString = oidc.user ? oidc.user.profile.idString : null;
    const headers = getHeaders(oidc.user);

    // First get environment config
    const environment = yield call([
        leerplatformSettingsStore,
        leerplatformSettingsStore.loadEnvironmentConfig,
    ]);

    // Then fetch portal settings
    const portalSettings = yield call(fetchPortalSettings, environment.settingsUrl, headers);

    if (portalSettings.notFound) {
        yield put({
            type: 'MINERVASETTINGS_NOT_FOUND',
            payload: {
                ...portalSettings,
            },
        });
        return;
    }

    if (portalSettings.forceLogout) {
        const settings = {
            settings: {},
            forceLogout: true,
        };

        yield put({
            type: 'MINERVASETTINGS_RECEIVED',
            payload: {
                ...settings,
            },
        });
        return;
    }

    // convert enabledFeatureFlags to object with keys as truthy booleans
    portalSettings.enabledFeatureFlags = portalSettings.enabledFeatureFlags.length
        ? Object.assign(...portalSettings.enabledFeatureFlags.map((x) => ({[x]: true})))
        : {};

    if (!portalSettings.enabledFeatureFlags.HLIntegrationEnabled) {
        delete environment.hlUrl;
    }

    portalSettings.planningIntegrationEnabled =
        portalSettings.planningIntegrationEnabled ||
        portalSettings.enabledFeatureFlags.planningIntegration;

    if (!portalSettings.planningIntegrationEnabled) {
        delete environment.planningIntegrationApiUrl;
    }

    let rights = [];
    let departmentsWhereManager = [];
    let departmentsWhereManagerRights = [];
    let domain = {};
    let isManager = false;

    if (oidc.user) {
        if (window.appInsights && idString) {
            window.appInsights.setAuthenticatedUserContext(
                idString.replace('\\', '\\\\').replace(/[,;=| ]+/g, '_'),
            );

            if (!window.appInsights_roleSet) {
                const telemetryInitializer = (envelope) => {
                    envelope.tags['ai.cloud.role'] = 'SDB Leerplatform';
                    envelope.tags['ai.cloud.roleInstance'] = portalSettings.portalIdentifier;
                };

                window.appInsights.addTelemetryInitializer(telemetryInitializer);
                window.appInsights_roleSet = true;
            }
        }

        // Start fetching skills settings in the background
        yield fork(fetchSkillsSettingsAndUpdate, environment, portalSettings, headers);

        let departmentsWhereManagerResult;

        [rights, departmentsWhereManagerResult] = yield all([
            call(fetchUserRights, portalSettings, environment, headers),
            call(fetchDepartmentsWhereManager, portalSettings, environment, headers),
        ]);

        departmentsWhereManager = departmentsWhereManagerResult.departmentIdsWhereManager;
        departmentsWhereManagerRights =
            departmentsWhereManagerResult.departmentIdsWhereManagerRights;

        isManager = departmentsWhereManagerRights && departmentsWhereManagerRights.length > 0;

        if (isManager) {
            rights = _injectManagerRights(rights);
        }

        const lowerIdString = idString.toLowerCase();
        const tempDomain = portalSettings.signInDomains.find((x) =>
            lowerIdString.startsWith(`//${x.domainId.toLowerCase()}//`),
        );

        if (tempDomain) {
            domain = tempDomain;
        }
    }

    const settings = {
        ...portalSettings,
        ...environment,
        notFound: false,
        headers,
        idString,
        isManager,
        departmentsWhereManager,
        departmentsWhereManagerRights,
        hideClassroomParticipants: domain.hideClassroomParticipants,
        profileReadonly: domain.profileReadonly,
        anonymizeParticipantsFromOtherDomains: domain.anonymizeParticipantsFromOtherDomains,
        portfolioLandingSuffix: portalSettings.skillsEnabled ? '/skills/list' : '/progress/list',
        rights,
        departmentSkillsMatrixViewEnabled: true,
    };

    yield put({
        type: 'MINERVASETTINGS_RECEIVED',
        payload: {...settings},
    });
}

function _injectManagerRights(rights) {
    const result = new Set(rights);

    result.add('classroom.manage_enrollments');

    return Array.from(result);
}

function* fetchPortalSettings(settingsUrl, headers) {
    const response = yield call(fetch, `${settingsUrl}/api/settings`, {
        headers,
    });

    if (response.status === 404) {
        return {
            settings: {},
            notFound: true,
        };
    }

    return yield call([response, response.json]);
}

function* fetchSkillsSettings(skillsApiUrl, portalIdentifier, headers) {
    const response = yield call(fetch, `${skillsApiUrl}/api/6.0/settings/${portalIdentifier}`, {
        headers,
    });

    if (response.status === 404) {
        return {};
    }

    return yield call([response, response.json]);
}

function* fetchUserRights(portalSettings, environment, headers) {
    if (portalSettings.isAdmin) {
        return ['*']; // use '*' as special case for all rights
    }

    if (headers.Authorization) {
        const rightsResponse = yield call(
            fetch,
            `${environment.accessManagementApiUrl}/api/rights/${portalSettings.portalIdentifier}/identity`,
            {
                headers,
            },
        );

        return yield call([rightsResponse, rightsResponse.json]);
    }

    return [];
}

function* fetchDepartmentsWhereManager(portalSettings, environment, headers) {
    const response = yield call(
        fetch,
        `${environment.academyDepartmentsUrl}/api/1.0/departmentsWhereManager/${portalSettings.portalIdentifier}`,
        {
            headers,
        },
    );

    const result = yield call([response, response.json]);

    return result;
}

// export const saveSettings = (settings) => async (dispatch, getState) => {
//     const {minervaSettings, oidc} = getState();
//     const url = `${minervaSettings.settingsUrl}/api/settings/${minervaSettings.portalIdentifier}`;
//     const body = JSON.stringify(settings);

//     await dispatch({
//         type: SAVE_SETTINGS,
//         async payload() {
//             // console.log("save starting");
//             const response = await fetch(url, {
//                 headers: {
//                     ...getHeaders(oidc.user),
//                     'Content-Type': 'application/json',
//                 },
//                 method: 'PUT',
//                 body: body,
//             });

//             const savedSettings = await response.json();

//             return savedSettings;
//         },
//     });
// };

// export const addAdmin = (user) => async (dispatch, getState) => {
//     const {minervaSettings, oidc} = getState();
//     const url = `${minervaSettings.settingsUrl}/api/admin/${minervaSettings.portalIdentifier}`;
//     dispatch({
//         type: ADD_ADMIN,
//         async payload() {
//             const response = await fetch(url, {
//                 headers: {
//                     ...getHeaders(oidc.user),
//                     'Content-Type': 'application/json',
//                 },
//                 method: 'PUT',
//                 body: JSON.stringify(user),
//             });

//             await dispatch(fetchSettings(oidc.user));
//         },
//     });
// };

// export const addReviewer = (user) => async (dispatch, getState) => {
//     const {minervaSettings, oidc} = getState();
//     const url = `${minervaSettings.settingsUrl}/api/departmentReviewer/${minervaSettings.portalIdentifier}`;

//     dispatch({
//         type: ADD_REVIEWER,
//         async payload() {
//             const response = await fetch(url, {
//                 headers: {
//                     ...getHeaders(oidc.user),
//                     'Content-Type': 'application/json',
//                 },
//                 method: 'PUT',
//                 body: JSON.stringify(user),
//             });

//             await dispatch(fetchSettings(oidc.user));
//         },
//     });
// };

// export const removeAdmin = (idString) => async (dispatch, getState) => {
//     const {minervaSettings, oidc} = getState();
//     const url = `${minervaSettings.settingsUrl}/api/admin/${minervaSettings.portalIdentifier}?idString=${encodeURIComponent(idString)}`;

//     dispatch({
//         type: REMOVE_ADMIN,
//         async payload() {
//             const response = await fetch(url, {
//                 headers: {
//                     ...getHeaders(oidc.user),
//                     'Content-Type': 'application/json',
//                 },
//                 method: 'DELETE',
//             });

//             await dispatch(fetchSettings(oidc.user));
//         },
//     });
// };

// export const removeReviewer = (idString) => async (dispatch, getState) => {
//     const {minervaSettings, oidc} = getState();
//     const url = `${minervaSettings.settingsUrl}/api/departmentReviewer/${minervaSettings.portalIdentifier}?idString=${encodeURIComponent(idString)}`;

//     dispatch({
//         type: REMOVE_REVIEWER,
//         async payload() {
//             const response = await fetch(url, {
//                 headers: {
//                     ...getHeaders(oidc.user),
//                     'Content-Type': 'application/json',
//                 },
//                 method: 'DELETE',
//             });

//             await dispatch(fetchSettings(oidc.user));
//         },
//     });
// };

function* setDepartmentMatrixVisible() {
    const {minervaSettings} = yield select();

    yield put({
        type: 'DEPARTMENT_MATRIX_VISIBLE_SET',
        payload: {
            departmentSkillsMatrixViewEnabled: !minervaSettings.departmentSkillsMatrixViewEnabled,
        },
    });
}

export function* settingSaga() {
    yield takeLatest('MINERVASETTINGS_REQUESTED', doFetchSettings);
    yield takeLatest('TOGGLE_DEPARTMENT_MATRIX_VISIBLE', setDepartmentMatrixVisible);
}
